import type { BadgeVariant } from '@/components/ui'
import type { AttendanceStatus } from '@/features/attendance/types/attendance.types'

/** Maps attendance status to Badge variant for consistent UI. */
export function attendanceStatusBadgeVariant(status: AttendanceStatus): BadgeVariant {
  switch (status) {
    case 'present':
      return 'success'
    case 'late':
      return 'warning'
    case 'absent':
      return 'danger'
    case 'half-day':
    case 'on-leave':
    default:
      return 'neutral'
  }
}

/** Known error codes returned by the attendance API. */
export const ATTENDANCE_ERROR_CODES = {
  SHIFT_ASSIGNMENT_REQUIRED: 'SHIFT_ASSIGNMENT_REQUIRED',
  INVALID_SHIFT_ASSIGNMENT: 'INVALID_SHIFT_ASSIGNMENT',
  SHIFT_NOT_FOUND: 'SHIFT_NOT_FOUND',
  TIMER_ALREADY_RUNNING: 'TIMER_ALREADY_RUNNING',
  NO_ACTIVE_TIMER: 'NO_ACTIVE_TIMER',
  BREAK_ALREADY_STARTED: 'BREAK_ALREADY_STARTED',
  BREAK_NOT_ENDED: 'BREAK_NOT_ENDED',
  NO_ACTIVE_BREAK: 'NO_ACTIVE_BREAK',
  ALREADY_CLOCKED_OUT: 'ALREADY_CLOCKED_OUT',
  DAY_ALREADY_COMPLETED: 'DAY_ALREADY_COMPLETED',
  TOO_EARLY_TO_CLOCK_IN: 'TOO_EARLY_TO_CLOCK_IN',
  SHIFT_ALREADY_ENDED: 'SHIFT_ALREADY_ENDED',
} as const

const ERROR_MESSAGES: Record<string, string> = {
  [ATTENDANCE_ERROR_CODES.SHIFT_ASSIGNMENT_REQUIRED]:
    'Select a shift assignment for today.',
  [ATTENDANCE_ERROR_CODES.INVALID_SHIFT_ASSIGNMENT]:
    'Invalid or missing shift assignment.',
  [ATTENDANCE_ERROR_CODES.SHIFT_NOT_FOUND]: 'Shift not found.',
  [ATTENDANCE_ERROR_CODES.TIMER_ALREADY_RUNNING]: 'Already clocked in.',
  [ATTENDANCE_ERROR_CODES.NO_ACTIVE_TIMER]: 'No active session.',
  [ATTENDANCE_ERROR_CODES.BREAK_ALREADY_STARTED]: 'Break already started.',
  [ATTENDANCE_ERROR_CODES.BREAK_NOT_ENDED]: 'End your current break first.',
  [ATTENDANCE_ERROR_CODES.NO_ACTIVE_BREAK]: 'No active break.',
  [ATTENDANCE_ERROR_CODES.ALREADY_CLOCKED_OUT]: 'Already clocked out.',
  [ATTENDANCE_ERROR_CODES.DAY_ALREADY_COMPLETED]:
    'You have already ended your day. A manager must reopen it to clock in again.',
  [ATTENDANCE_ERROR_CODES.TOO_EARLY_TO_CLOCK_IN]:
    'It is too early to clock in for this shift.',
  [ATTENDANCE_ERROR_CODES.SHIFT_ALREADY_ENDED]:
    'This shift has ended. Request a time correction from your manager.',
}

interface AttendanceErrorData {
  shiftStartAt?: string
  shiftStartTime?: string
  shiftEndAt?: string
  shiftEndTime?: string
  maxEarlyMinutes?: number
  minutesUntilAllowed?: number
  lateCutoffMinutes?: number
}

function formatDuration(totalMinutes: number): string {
  if (totalMinutes <= 0) return '0m'
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

/**
 * Builds a context-rich message when the error payload includes shift-window
 * details (start/end times, cutoff minutes). Falls back to the static message.
 */
export function getAttendanceErrorMessageWithData(
  code: string | undefined,
  data?: AttendanceErrorData,
  defaultMessage: string = 'Something went wrong',
): string {
  if (!code) return defaultMessage
  if (code === ATTENDANCE_ERROR_CODES.TOO_EARLY_TO_CLOCK_IN && data) {
    const start = data.shiftStartTime
    const wait = data.minutesUntilAllowed
    if (start && wait != null && wait > 0) {
      return `Shift starts at ${start}. You can clock in in ${formatDuration(wait)}.`
    }
    if (start) return `Shift starts at ${start}.`
  }
  if (code === ATTENDANCE_ERROR_CODES.SHIFT_ALREADY_ENDED && data) {
    if (data.shiftEndTime) {
      return `Shift ended at ${data.shiftEndTime}. Request a time correction.`
    }
  }
  return ERROR_MESSAGES[code] ?? defaultMessage
}

/**
 * Returns a user-facing message for an attendance API error code, or the default message.
 */
export function getAttendanceErrorMessage(
  code: string | undefined,
  defaultMessage: string = 'Something went wrong'
): string {
  if (!code) return defaultMessage
  return ERROR_MESSAGES[code] ?? defaultMessage
}
